/**
 * @file ContractEndDateSectionView.tsx
 * @description Pure presentation for the dedicated Contract End Date
 *   section (SE-4881). Renders the formatted end date inline (e.g.
 *   "30 Jun 2027") with a pencil affordance, or the standard
 *   empty-state copy when the extraction has no end date. In edit mode
 *   it renders an inline input + save/cancel controls mirroring
 *   `LineItemInlineEditRow` (the same `bg-surface-info-subtle` highlight, Check/X
 *   icon buttons). Unlike the line-item rows, this section has NO
 *   "+ Add line" affordance and NO delete icon — there is exactly one
 *   end date per extraction. All editing affordances are suppressed
 *   when `readOnly` is true.
 * @module features/contracts/components/ContractEndDateSection/ContractEndDateSectionView
 */

import { Check, Pencil, X } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Input } from "@/components/atoms/Input";
import { Text } from "@/components/atoms/Text";

/** Props for {@link ContractEndDateSectionView}. */
export interface ContractEndDateSectionViewProps {
  /**
   * Section heading label (e.g. "Contract End Date"). Rendered above the
   * value so the section is self-labelling, consistent with how the
   * collapsible category sections show their name (AC1).
   */
  readonly label: string;
  /** Formatted display date (e.g. "30 Jun 2027"), or null when absent. */
  readonly displayDate: string | null;
  /** Empty-state copy rendered when `displayDate` is null. */
  readonly emptyMessage: string;
  /**
   * Whether the edit form is showing — the section is in inline-edit mode AND
   * the reader may edit. Resolved by the Container so the View forks once
   * (SE-8019).
   */
  readonly isEditable: boolean;
  /** Whether the edit mutation is in flight. */
  readonly isSaving: boolean;
  /** Working draft value (raw YYYY-MM-DD) held while the user edits. */
  readonly draftText: string;
  /**
   * Inline error message shown beneath the input while editing — set
   * when the draft fails client-side YYYY-MM-DD validation or when the
   * save mutation reports failure. Null when there is no error.
   */
  readonly errorMessage: string | null;
  /** When true, every editing affordance is suppressed. */
  readonly readOnly: boolean;
  /** Invoked when the user taps the pencil to enter edit mode. */
  readonly onStartEdit: () => void;
  /** Invoked when the input value changes. */
  readonly onChangeDraft: (text: string) => void;
  /** Invoked when the user confirms the edit. */
  readonly onSaveEdit: () => void;
  /** Invoked when the user cancels the edit. */
  readonly onCancelEdit: () => void;
}

/**
 * Pure presentation for the Contract End Date section.
 * @param props - View props
 * @param props.label - Section heading rendered above the value
 * @param props.displayDate - Formatted date or null
 * @param props.emptyMessage - Empty-state copy
 * @param props.isEditable - Whether the edit form is showing
 * @param props.isSaving - Whether the edit mutation is in flight
 * @param props.draftText - Working draft value
 * @param props.errorMessage - Inline validation/save error, or null
 * @param props.readOnly - Suppresses every editing affordance when true
 * @param props.onStartEdit - Enter edit mode
 * @param props.onChangeDraft - Input change handler
 * @param props.onSaveEdit - Confirm handler
 * @param props.onCancelEdit - Cancel handler
 * @returns The rendered section
 */
/**
 * The section heading, above the value, so the section is self-labelling (AC1).
 * @param props - View props.
 * @returns The heading.
 */
const renderHeading = (
  props: ContractEndDateSectionViewProps
): React.ReactElement => (
  <Box testID="contract-end-date-label">
    <Text variant="caption-strong" className="uppercase text-content-muted">
      {props.label}
    </Text>
  </Box>
);

/**
 * The edit form: the draft input, save and cancel, and the last error.
 * @param props - View props.
 * @returns The edit form.
 */
const renderEditing = (
  props: ContractEndDateSectionViewProps
): React.ReactElement => (
  <Box testID="contract-end-date-section">
    <Stack className="gap-1">
      {renderHeading(props)}
      <Stack
        direction="horizontal"
        className="items-center gap-2 rounded-none bg-surface-info-subtle p-2"
      >
        <Input
          size="sm"
          variant="outline"
          isDisabled={props.isSaving}
          className="flex-1"
          field={{
            testID: "contract-end-date-input",
            placeholder: "YYYY-MM-DD",
            value: props.draftText,
            onChangeText: props.onChangeDraft,
            editable: !props.isSaving,
          }}
        />
        <Button
          testID="contract-end-date-save"
          accessibilityLabel="Save contract end date"
          size="sm"
          variant="solid"
          tone="primary"
          icon={Check}
          onPress={props.onSaveEdit}
          isDisabled={props.isSaving}
          isLoading={props.isSaving}
        />
        <Button
          testID="contract-end-date-cancel"
          accessibilityLabel="Cancel contract end date edit"
          size="sm"
          variant="outline"
          tone="secondary"
          icon={X}
          onPress={props.onCancelEdit}
          isDisabled={props.isSaving}
        />
      </Stack>
      {props.errorMessage ? (
        <Box testID="contract-end-date-error">
          <Text variant="caption" className="text-needs-semantic-error-600">
            {props.errorMessage}
          </Text>
        </Box>
      ) : null}
    </Stack>
  </Box>
);

/**
 * The read-only value, with the edit affordance when the reader may edit.
 * @param props - View props.
 * @returns The value row.
 */
const renderReading = (
  props: ContractEndDateSectionViewProps
): React.ReactElement => (
  <Box testID="contract-end-date-section">
    <Stack className="gap-1">
      {renderHeading(props)}
      <Stack
        direction="horizontal"
        className="items-center justify-between gap-2 p-1"
      >
        {props.displayDate ? (
          <Box testID="contract-end-date-value">
            <Text variant="body" className="text-content-primary">
              {props.displayDate}
            </Text>
          </Box>
        ) : (
          <Box testID="contract-end-date-empty">
            <Text variant="body" className="italic text-content-muted">
              {props.emptyMessage}
            </Text>
          </Box>
        )}
        {props.readOnly ? null : (
          <Button
            testID="contract-end-date-edit-btn"
            accessibilityLabel="Edit contract end date"
            size="sm"
            variant="outline"
            tone="secondary"
            icon={Pencil}
            onPress={props.onStartEdit}
          />
        )}
      </Stack>
    </Stack>
  </Box>
);

/**
 * Renders the contract end date, editing or not.
 * @param props - View props.
 * @returns The section.
 */
const ContractEndDateSectionView = (props: ContractEndDateSectionViewProps) =>
  props.isEditable ? renderEditing(props) : renderReading(props);

ContractEndDateSectionView.displayName = "ContractEndDateSectionView";

export default memo(ContractEndDateSectionView);
